import { Component, OnInit, OnDestroy } from '@angular/core';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { SidebarService } from '../../services/sidebar.service';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { MaterialModule } from '../../modules/material/material.module';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CandidatesService } from '../../services/candidates.service';
import { Subscription, take } from 'rxjs';
import { Applicant, JobApplicationSearchRequest } from '../../models/applicant.model';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { 
  BackgroundCheckDialogComponent, 
  BackgroundCheckDialogData, 
  BackgroundCheckDialogResult 
} from '../background-check-dialog/background-check-dialog.component';
import { BackgroundCheckService } from '../../services/background-check.service';

type ColumnName = 'applied' | 'screening' | 'interview' | 'reference' | 'backgroundCheck' | 'hired';

@Component({
  selector: 'app-screening-board',
  imports: [SidebarComponent, MaterialModule, CommonModule, TranslateModule],
  templateUrl: './screening-board.component.html',
  styleUrl: './screening-board.component.scss'
})
export class ScreeningBoardComponent implements OnInit, OnDestroy {
  applied: Applicant[] = [];
  screening: Applicant[] = [];
  interview: Applicant[] = [];
  reference: Applicant[] = [];
  backgroundCheck: Applicant[] = [];
  hired: Applicant[] = [];
  loading: boolean = false;
  error: string | null = null;

  columnPages: Record<ColumnName, number> = {
    applied: 1,
    screening: 1,
    interview: 1,
    reference: 1,
    backgroundCheck: 1,
    hired: 1
  };

  columnLoading: Record<ColumnName, boolean> = {
    applied: false,
    screening: false,
    interview: false,
    reference: false,
    backgroundCheck: false,
    hired: false
  };

  columnHasMore: Record<ColumnName, boolean> = {
    applied: true,
    screening: true,
    interview: true,
    reference: true,
    backgroundCheck: true,
    hired: true
  };

  columnTotalCount: Record<ColumnName, number> = {
    applied: 0,
    screening: 0,
    interview: 0,
    reference: 0,
    backgroundCheck: 0,
    hired: 0
  };

  isDragOverMap: Record<ColumnName, boolean> = {
    applied: false,
    screening: false,
    interview: false,
    reference: false,
    backgroundCheck: false,
    hired: false
  };

  draggingCandidateId: number | null = null;

  private statusMapping = {
    applied: 1,
    screening: 2,
    interview: 3,
    reference: 4,
    hired: 5,
    backgroundCheck: 7
  };

  private subscription: Subscription = new Subscription();

  constructor(
    private candidatesService: CandidatesService,
    private router: Router,
    private authService: AuthService,
    private dialog: MatDialog,
    private backgroundCheckService: BackgroundCheckService,
    private sidebarService: SidebarService
  ) {}

  ngOnInit() {
    this.loadInitialData();
    this.subscription.add(
      this.authService.currentUserSubject.subscribe(data => {
        if (data) {
          let isSubscribed = localStorage.getItem('subscriptionStatus');
          if (!isSubscribed || isSubscribed == 'canceled' || isSubscribed == 'incomplete' || isSubscribed == 'incomplete_expired' || isSubscribed == 'unpaid' || isSubscribed == 'none') {
            this.router.navigate(['./subscription']);
            this.authService.isSubscribeDialogOpen = true;
            this.authService.showUnsubscribedMessage.next(true);
          }
        }
      })
    );

    // Auto-collapse sidebar when entering this page
    this.sidebarService.isDesktopCollapsed$.pipe(take(1)).subscribe(isCollapsed => {
      if (!isCollapsed) {
        this.sidebarService.toggleDesktopSidebar();
      }
    });
  }

  toggleMobileMenu() {
    this.sidebarService.toggleMobileMenu();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();

    // Expand sidebar back when leaving this page
    this.sidebarService.isDesktopCollapsed$.pipe(take(1)).subscribe(isCollapsed => {
      if (isCollapsed) {
        this.sidebarService.toggleDesktopSidebar();
      }
    });
  }

  private loadInitialData() {
    const columns: ColumnName[] = ['applied', 'screening', 'interview', 'reference', 'backgroundCheck', 'hired'];
    columns.forEach(column => {
      this.loadMoreForColumn(column, true);
    });
  }

  private loadMoreForColumn(column: ColumnName, isInitial: boolean = false) {
    if (this.columnLoading[column] || (!isInitial && !this.columnHasMore[column])) {
      return;
    }

    this.columnLoading[column] = true;
    const statusId = this.statusMapping[column];
    const currentPage = this.columnPages[column];
    const pageSize = 5;

    const loadedCandidateIds = this[column].map(c => c.id).filter(id => id !== undefined);

    const request: JobApplicationSearchRequest = {
      query: null,
      statusId: statusId,
      jobDetailsCreatedById: null,
      jobDetailsId: null,
      status: [statusId],
      dateFrom: null,
      dateTo: null,
      paging: {
        pageNumber: currentPage,
        pageSize: pageSize
      },
      sorting: {
        field: 1,
        sortOrder: 0
      }
    };

    this.subscription.add(
      this.candidatesService.searchJobApplications(request).pipe(
        take(1)
      ).subscribe({
        next: (response) => {
          const newItems = response.items || [];

          const filteredItems = newItems.filter(item =>
            !loadedCandidateIds.includes(item.id)
          );

          if (isInitial && ((response.totalCount ?? 0) > 1)) {
            this.columnTotalCount[column] = response.totalCount ?? filteredItems.length;
          }

          if (filteredItems.length === 0) {
            this.columnHasMore[column] = false;
          } else {
            this[column] = [...this[column], ...filteredItems];
            this.columnPages[column]++;

            if (filteredItems.length < 5) {
              this.columnHasMore[column] = false;
            }
          }

          this.columnLoading[column] = false;
        },
        error: (error) => {
          console.error(`Error loading more for ${column}:`, error);
          this.columnLoading[column] = false;

          if (isInitial) {
            this.error = `Failed to load ${column} candidates`;
          }
        }
      })
    );
  }

  onColumnScroll(event: Event, column: ColumnName) {
    const element = event.target as HTMLElement;
    const threshold = 50;

    if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold) {
      this.loadMoreForColumn(column);
    }
  }

  onDragStarted(candidateId: number | undefined) {
    if (candidateId !== undefined) {
      this.draggingCandidateId = candidateId;
    }
  }

  onDragEnded() {
    this.draggingCandidateId = null;
    Object.keys(this.isDragOverMap).forEach(key => {
      this.isDragOverMap[key as ColumnName] = false;
    });
  }

  onDragEntered(column: ColumnName) {
    this.isDragOverMap[column] = true;
  }

  onDragExited(column: ColumnName) {
    this.isDragOverMap[column] = false;
  }

  drop(event: CdkDragDrop<Applicant[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.updateSortOrders(event.container.data);
    } else {
      const candidate = event.previousContainer.data[event.previousIndex];
      const newStatusName = this.getStatusFromContainerId(event.container.id);
      const oldStatusName = this.getStatusFromContainerId(event.previousContainer.id);
      const newStatusId = this.statusMapping[newStatusName as keyof typeof this.statusMapping];

      if (candidate && candidate.id && newStatusId) {
        // Check if moving to background check column
        if (newStatusName === 'backgroundCheck') {
          this.handleBackgroundCheckDrop(event, candidate, oldStatusName as ColumnName, newStatusName);
        } else {
          this.performStandardDrop(event, candidate, oldStatusName as ColumnName, newStatusName, newStatusId);
        }
      }
    }
  }

  getCandidateSkills(candidate: any): string[] {
    let tagsString!: string;
    
    if (candidate?.jobApplicationSections) {
      candidate.jobApplicationSections.forEach((section: any) => {
        if (section.jobApplicationSectionProperties) {
          section.jobApplicationSectionProperties.forEach((field: any) => {
            if (field.type === 'tags') {
              tagsString = field.stringValue;
            }
          });
        }
      });
    }
    
    if (tagsString) {
      return tagsString.split(',').map((item: string) => item.trim());
    }
    
    return [];
  }

  private handleBackgroundCheckDrop(
    event: CdkDragDrop<Applicant[]>,
    candidate: Applicant,
    oldStatusName: ColumnName,
    newStatusName: string
  ) {
    const statusMapping = this.statusMapping;
    
    // Check if background check already exists for this candidate
    this.backgroundCheckService.getStatus(candidate.id).pipe(take(1)).subscribe({
      next: (backgroundCheckStatus) => {
        if (backgroundCheckStatus) {
          // Background check already exists - proceed with move without dialog
          const newStatusId = statusMapping[newStatusName as keyof typeof statusMapping];
          this.performStandardDrop(event, candidate, oldStatusName, newStatusName, newStatusId);
        } else {
          // No background check exists - show confirmation dialog
          const dialogData: BackgroundCheckDialogData = {
            jobApplicationId: candidate.id,
            candidateName: `${candidate.firstName} ${candidate.lastName}`,
            candidateEmail: candidate.emailAddress
          };

          const dialogRef = this.dialog.open(BackgroundCheckDialogComponent, {
            width: '450px',
            disableClose: true,
            data: dialogData
          });

          dialogRef.afterClosed().subscribe((result: BackgroundCheckDialogResult) => {
            if (result?.success) {
              // User confirmed - proceed with the move
              const newStatusId = statusMapping[newStatusName as keyof typeof statusMapping];
              this.performStandardDrop(event, candidate, oldStatusName, newStatusName, newStatusId);
            } else {
              // User cancelled or error - do nothing, card stays in original position
              console.log('Background check cancelled or failed - candidate not moved');
            }
          });
        }
      },
      error: (error) => {
        console.error('Error checking background check status:', error);
        // On error, proceed without dialog (safer to allow the move)
        const newStatusId = statusMapping[newStatusName as keyof typeof statusMapping];
        this.performStandardDrop(event, candidate, oldStatusName, newStatusName, newStatusId);
      }
    });
  }

  private performStandardDrop(
    event: CdkDragDrop<Applicant[]>,
    candidate: Applicant,
    oldStatusName: ColumnName,
    newStatusName: string,
    newStatusId: number
  ) {
    const originalContainer = event.previousContainer.data;
    const originalIndex = event.previousIndex;
    const originalStatus = { ...candidate.status };

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    this.updateColumnCounts(oldStatusName, newStatusName as ColumnName);
    this.resetColumnPagination(oldStatusName, newStatusName as ColumnName);

    candidate.status = {
      id: newStatusId,
      name: this.getStatusDisplayName(newStatusName)
    };

    this.updateCandidateStatusAPI(candidate, newStatusId, event.currentIndex, {
      originalContainer,
      originalIndex,
      originalStatus,
      fromColumn: oldStatusName,
      toColumn: newStatusName as ColumnName
    });
  }

  private updateCandidateStatusAPI(
    candidate: Applicant,
    newStatusId: number,
    newIndex: number,
    rollbackData: {
      originalContainer: Applicant[],
      originalIndex: number,
      originalStatus: any,
      fromColumn: ColumnName,
      toColumn: ColumnName
    }
  ) {
    this.loading = true;
    this.candidatesService.updateCandidateStatus(
      candidate.id,
      newStatusId,
      newIndex
    ).pipe(take(1)).subscribe({
      next: () => {
        this.loading = false;
        this.updateSortOrders(this[rollbackData.toColumn]);
      },
      error: (error) => {
        console.error('Failed to update candidate status:', error);
        this.loading = false;

        transferArrayItem(
          this[rollbackData.toColumn],
          rollbackData.originalContainer,
          this[rollbackData.toColumn].indexOf(candidate),
          rollbackData.originalIndex
        );

        candidate.status = rollbackData.originalStatus;

        this.rollbackColumnCounts(rollbackData.fromColumn, rollbackData.toColumn);

        if (error.status === 402) {
          this.authService.showUnsubscribedMessage.next(true);
          this.authService.isSubscribeDialogOpen = true;
        } else {
          this.error = error.error?.message || 'Failed to update candidate status. Please try again.';
        }
      }
    });
  }

  private updateColumnCounts(fromColumn: ColumnName, toColumn: ColumnName) {
    if (this.columnTotalCount[fromColumn] > 0) {
      this.columnTotalCount[fromColumn]--;
    }
    this.columnTotalCount[toColumn]++;
  }

  private rollbackColumnCounts(fromColumn: ColumnName, toColumn: ColumnName) {
    if (this.columnTotalCount[toColumn] > 0) {
      this.columnTotalCount[toColumn]--;
    }
    this.columnTotalCount[fromColumn]++;
  }

  private updateSortOrders(columnData: Applicant[]) {
    columnData.forEach((candidate, index) => {
      if (candidate.id && candidate.status?.id) {
        this.loading = true;
        this.candidatesService.updateCandidateStatus(
          candidate.id,
          candidate.status.id,
          index
        ).pipe(take(1)).subscribe({
          next: () => this.loading = false,
          error: (error) => console.error('Failed to update sort order:', error)
        });
      }
    });
  }

  private getStatusFromContainerId(containerId: string): string {
    const cleanId = containerId.replace(/^cdk-drop-list-\d+-/, '');
    const containerStatusMap: { [key: string]: string } = {
      'applied': 'applied',
      'screening': 'screening',
      'interview': 'interview',
      'reference': 'reference',
      'backgroundCheck': 'backgroundCheck',
      'hired': 'hired'
    };

    return containerStatusMap[cleanId] || 'applied';
  }

  private getStatusDisplayName(statusName: string): string {
    const displayNames: { [key: string]: string } = {
      'applied': 'Applied',
      'screening': 'Screening',
      'interview': 'Interview',
      'reference': 'ReferenceCheck',
      'backgroundCheck': 'BackgroundCheck',
      'hired': 'Hired'
    };

    return displayNames[statusName] || 'Applied';
  }

  shouldShowPlaceholder(column: ColumnName): boolean {
    const columnData = this[column] as Applicant[];
    return columnData.length === 0 && !this.isDragOverMap[column] && !this.columnLoading[column];
  }

  trackByFn(index: number, item: Applicant) {
    return item.id;
  }

  refreshBoard() {
    this.applied = [];
    this.screening = [];
    this.interview = [];
    this.reference = [];
    this.backgroundCheck = [];
    this.hired = [];

    Object.keys(this.columnPages).forEach(key => {
      this.columnPages[key as ColumnName] = 1;
      this.columnHasMore[key as ColumnName] = true;
    });

    this.loadInitialData();
  }

  private resetColumnPagination(fromColumn: ColumnName, toColumn: ColumnName) {
    const fromColumnCurrentCount = this[fromColumn].length;
    const fromColumnTotalCount = this.columnTotalCount[fromColumn];

    if (fromColumnCurrentCount < fromColumnTotalCount && fromColumnCurrentCount < 5) {
      this.loadRemainingCandidatesForColumn(fromColumn);
    }

    const fromColumnPageSize = 5;
    const fromColumnExpectedPage = Math.floor(fromColumnCurrentCount / fromColumnPageSize) + 1;

    if (this.columnPages[fromColumn] > fromColumnExpectedPage) {
      this.columnPages[fromColumn] = fromColumnExpectedPage;
    }

    this.columnHasMore[fromColumn] = this.columnTotalCount[fromColumn] > fromColumnCurrentCount;

    const toColumnCurrentCount = this[toColumn].length;
    this.columnHasMore[toColumn] = this.columnTotalCount[toColumn] > toColumnCurrentCount;
  }

  private loadRemainingCandidatesForColumn(column: ColumnName) {
    const currentCount = this[column].length;
    const totalCount = this.columnTotalCount[column];
    const pageSize = 5;

    const spaceAvailable = pageSize - (currentCount % pageSize);
    const remainingItems = totalCount - currentCount;

    if (remainingItems > 0 && spaceAvailable > 0 && !this.columnLoading[column]) {
      console.log(`Loading remaining candidates for ${column}: current=${currentCount}, total=${totalCount}, space=${spaceAvailable}`);

      this.columnLoading[column] = true;
      const statusId = this.statusMapping[column];
      const loadedCandidateIds = this[column].map(c => c.id).filter(id => id !== undefined);

      const request: JobApplicationSearchRequest = {
        query: null,
        statusId: statusId,
        jobDetailsCreatedById: null,
        jobDetailsId: null,
        status: [statusId],
        dateFrom: null,
        dateTo: null,
        paging: {
          pageNumber: 1,
          pageSize: Math.min(remainingItems, 10)
        },
        sorting: {
          field: 1,
          sortOrder: 0
        }
      };

      this.subscription.add(
        this.candidatesService.searchJobApplications(request).pipe(
          take(1)
        ).subscribe({
          next: (response) => {
            const newItems = response.items || [];

            const filteredItems = newItems.filter(item =>
              !loadedCandidateIds.includes(item.id)
            );

            if (filteredItems.length > 0) {
              this[column] = [...this[column], ...filteredItems];

              const newCurrentCount = this[column].length;
              this.columnPages[column] = Math.floor(newCurrentCount / pageSize) + 1;
              this.columnHasMore[column] = newCurrentCount < this.columnTotalCount[column];
            }

            this.columnLoading[column] = false;
          },
          error: (error) => {
            console.error(`Error loading remaining candidates for ${column}:`, error);
            this.columnLoading[column] = false;
          }
        })
      );
    }
  }
}